import { ReactNode, forwardRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '../ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export interface IShadCnSelectOption {
  label: ReactNode;
  value: any;
}

interface IShadCnFormSelectProps {
  name: string;
  placeholder?: string;
  disabled?: boolean;
  label?: ReactNode;
  className?: string;
  options: IShadCnSelectOption[];
  onChange?: (value: any) => void;
  required?: boolean;
  transformValueCallback?: (value: any) => any;
  renderValue?: (value: IShadCnSelectOption) => ReactNode;
  CustomSelectTrigger?: (props: any) => ReactNode;
}

const ShadCnFormSelect = forwardRef<HTMLButtonElement, IShadCnFormSelectProps>(
  (
    {
      name,
      placeholder = '',
      disabled = false,
      label = '',
      className = '',
      options = [],
      onChange,
      required = false,
      transformValueCallback,
      renderValue,
      CustomSelectTrigger,
    },
    ref,
  ) => {
    const form = useFormContext();

    // CRITICAL FIX: Moved getSelectedLabel inside render to use field.value instead of form.watch()
    // Using form.watch() inside render caused infinite re-render loops because:
    // 1. form.watch() subscribes to form changes
    // 2. Any form change triggers re-render of this component
    // 3. Re-render calls form.watch() again, creating a subscription loop
    // See: https://github.com/react-hook-form/react-hook-form/issues/4646

    return (
      <FormField
        control={form.control}
        name={name}
        render={({ field }) => {
          const hasNumericValues = options.some((option) => typeof option.value === 'number');
          const selectValue =
            field.value === undefined || field.value === null ? '' : String(field.value);

          // Use field.value (already available from FormField) instead of form.watch(name)
          const getSelectedLabel = () => {
            const selectedOption = options.find(
              (option) => String(option.value) === String(field.value),
            );
            return selectedOption?.label || '';
          };

          return (
            <FormItem className={className}>
              {!!label && (
                <FormLabel className='text-gray-700'>
                  {label} {required && <span className='text-red-500'>*</span>}
                </FormLabel>
              )}
              <Select
                name={name}
                onValueChange={(value) => {
                  const formValue = transformValueCallback
                    ? transformValueCallback(value)
                    : hasNumericValues
                      ? Number(value)
                      : value;
                  field.onChange(formValue);
                  onChange?.(formValue);
                }}
                value={selectValue}
                disabled={disabled}
              >
                <FormControl>
                  {CustomSelectTrigger ? (
                    <CustomSelectTrigger disabled={disabled} option={field.value}>
                      {renderValue ? (
                        <SelectValue placeholder={placeholder}>{getSelectedLabel()}</SelectValue>
                      ) : (
                        <SelectValue placeholder={placeholder} />
                      )}
                    </CustomSelectTrigger>
                  ) : (
                    <SelectTrigger ref={ref} disabled={disabled}>
                      {renderValue ? (
                        <SelectValue placeholder={placeholder}>{getSelectedLabel()}</SelectValue>
                      ) : (
                        <SelectValue placeholder={placeholder} />
                      )}
                    </SelectTrigger>
                  )}
                </FormControl>
                <SelectContent>
                  {options?.map((option, index) => {
                    return (
                      <SelectItem value={String(option?.value)} key={index}>
                        {renderValue ? renderValue(option) : option.label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          );
        }}
      />
    );
  },
);

ShadCnFormSelect.displayName = 'ShadCnFormSelect';

export default ShadCnFormSelect;
